/**
 * Controller class for managing and displaying clue boards in the UI.
 *
 * @class
 * @param {Object} [options={}] - Optional configuration for clue board elements.
 * @param {HTMLElement} [options.clueBoard1] - DOM element for clue board 1.
 * @param {HTMLElement} [options.clueBoard2] - DOM element for clue board 2.
 * @param {HTMLElement} [options.clueBoard3] - DOM element for clue board 3.
 * @param {HTMLElement} [options.clueBoard4] - DOM element for clue board 4.
 * @param {HTMLElement} [options.clueBoard5] - DOM element for clue board 5.
 * @param {HTMLElement} [options.clueBoard6] - DOM element for clue board 6.
 *
 * @property {number} CLUEBOARD_MAX - Maximum number of clue boards.
 * @property {number} CLUEBOARD_WIDTH - Width of each clue board.
 * @property {number} clueBoardElemCount - Current count of clue board elements.
 * @property {HTMLElement[]} clueBoardElems - Array of clue board DOM elements.
 *
 * @method buildClueBoards
 * Builds the clue boards based on the provided financial model.
 * @param {FinModel} finModel - The fillin model containing clue data.
 *
 * @method displayClues
 * Displays clues on the clue boards based on the provided financial model.
 * @param {FinModel} finModel - The fillin model containing clue data.
 */
// import { FinModel } from "./finmodel";
// import { GridModel } from "./gridmodel";
import { ClueBoard } from "./ClueBoard";

export class ClueBoardsController {
  constructor(clueSegmentElements, cluesContainerElement) {
    if (clueSegmentElements.some((elem) => elem === null)) {
      throw new Error(
        "All six clueBoard elements must be provided and non-null."
      );
    }
    this.CLUEBOARD_MAX = 9;
    this.CLUEBOARD_WIDTH = 70;
    this.clueSegmentElements = clueSegmentElements;
    this.clueSegmentCount = clueSegmentElements.length;
    this.clueBoardCount = 0;
    this.clueBoards = [];

    // Store reference to parent container for dynamic element creation
    this.cluesContentContainer = cluesContainerElement;
  }

  /**
   * Create a new clue board element dynamically if needed.
   * @returns {HTMLElement} - The newly created clue board element
   */
  createClueSegment() {
    if (!this.cluesContentContainer) {
      console.error(
        "[CLUEBOARDSCONTROLLER] No parent container found for creating clue boards"
      );
      return null;
    }

    const newClueSegment = document.createElement("div");
    newClueSegment.className = "clues-section";
    newClueSegment.id = `clueboard${this.clueSegmentElements.length + 1}`;

    // Append to the clues-content container
    this.cluesContentContainer.appendChild(newClueSegment);

    console.log(
      `[CLUEBOARDSCONTROLLER] Created dynamic clue board: ${newClueSegment.id}`
    );

    return newClueSegment;
  }

  /**
   * Build up the HTML elements containing the clue words.
   * @param {FinModel|null} finModel - a model of the puzzle data
   * @param {GridModel|null} gridModel - a model of grid
   */
  buildClueBoards(finModel, gridModel) {
    if (!finModel) return;

    // FIXME: this seems like overkill for 8 items which are numbered to begin with
    const sortedWords = finModel.getSortedWordsByLength();
    let sortedKeysNames = Object.keys(sortedWords).sort((a, b) => {
      const extractNum = (s) => {
        const m = s.match(/\d+/);
        return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
      };
      const na = extractNum(a);
      const nb = extractNum(b);
      if (na !== nb) return na - nb;
      return a.localeCompare(b);
    });

    sortedKeysNames.forEach((wordLettersSizeKey, idx) => {
      console.log("[CLUEBOARDSCONTROLLER] word length index", idx);

      // Get existing clue board element or create a new one if needed
      let clueSegment = this.clueSegmentElements[idx];

      if (!clueSegment) {
        // Need to create a new clue board element dynamically
        clueSegment = this.createClueSegment();

        if (clueSegment) {
          // Add to our tracked elements array
          this.clueSegmentElements.push(clueSegment);
          this.clueSegmentCount++;
          console.log(
            `[CLUEBOARDSCONTROLLER] Added dynamic clue board ${idx + 1}, total: ${this.clueSegmentCount}`
          );
        } else {
          console.error(
            `[CLUEBOARDSCONTROLLER] Failed to create clue board for index ${idx}`
          );
          return; // Skip this word size group
        }
      }

      let wordSize = parseInt(wordLettersSizeKey.replace(/^\D+/g, ""), 10);
      if (clueSegment) {
        let sizedWordList = sortedWords[wordLettersSizeKey] || [];
        if (sizedWordList.length != 0) {
          let innerText = `<h3>${wordSize} Letters</h3>`;
          clueSegment.innerHTML = innerText;

          if (gridModel && typeof gridModel.getWordDictionary === "function") {
            const gridWordDict = gridModel.getWordDictionary();
            const matchingGridWords = sizedWordList
              .map((word) => gridWordDict[word])
              .filter(Boolean);
            // Create ClueBoard with proper argument order: words, gridWords, domElement, controller
            const currentClueBoard = new ClueBoard(
              sizedWordList,
              matchingGridWords,
              clueSegment,
              this
            );
            this.clueBoards.push(currentClueBoard);
            this.clueBoardCount = idx;
            currentClueBoard.render();
          }
        }
      }
    });

    console.log(
      `[CLUEBOARDSCONTROLLER] Total clue boards created: ${this.clueSegmentElements.length}`
    );
  }

  /**
   * Returns the array of ClueBoard instances.
   * @returns {ClueBoard[]} Array of clue board instances.
   */
  getAllClueBoards() {
    return this.clueBoards;
  }

  /**
   * Find and return the ClueBoard that contains a ClueWord matching the given value.
   * @param {string} wordValue - The word value to search for
   * @returns {ClueBoard|null} The ClueBoard containing the matching ClueWord, or null if not found
   */
  getClueBoardContainingClueText(wordValue) {
    for (const clueBoard of this.clueBoards) {
      if (clueBoard.getClueWordByValue(wordValue)) {
        return clueBoard;
      }
    }
    return null;
  }

  /**
   * Find and return the ClueWord object that matches the given word value.
   * Cycles through all clue boards to find the matching ClueWord.
   * @param {string} wordValue - The word value to search for
   * @returns {ClueWord|null} The ClueWord object matching the value, or null if not found
   */
  getClueWordByValue(wordValue) {
    for (const clueBoard of this.clueBoards) {
      const clueWord = clueBoard.getClueWordByValue(wordValue);
      if (clueWord) {
        return clueWord;
      }
    }
    return null;
  }

  /**
   * Gather all clue word objects from all clue boards into a single array.
   * @returns {Array} Array of all clue word objects from all clue boards.
   */
  getAllClueWords() {
    return this.clueBoards.flatMap((clueBoard) =>
      clueBoard.getClueWords ? clueBoard.getClueWords() : []
    );
  }

  /**
   * Count the total number of used words across all clue boards.
   * @returns {number} Total count of used words.
   */
  countAllUsedWords() {
    return this.clueBoards.reduce((sum, clueBoard) => {
      if (typeof clueBoard.getUsedWordCount === "function") {
        return sum + clueBoard.getUsedWordCount();
      }
      return sum;
    }, 0);
  }

  /**
   * Sum the clue board counts.
   * @returns {number} The sum of clue board counts.
   */
  sumClueWordCounts() {
    const allClueWords = this.getAllClueWords();
    return allClueWords.filter((wordObj) => {
      if (typeof wordObj.isUsed === "function") {
        return wordObj.isUsed();
      } else if (typeof wordObj.usedTimes === "number") {
        return wordObj.usedTimes > 0;
      }
      return false;
    }).length;
  }

  /**
   * Reset the clue board clues to unsolved state.
   */
  doReset() {
    console.log("Clueboards Controller Reset");
  }

  /**
   * Handler called by child clue boards when a clue word is filled.
   * @param {Object} clueWordObj - The clue word object that was filled.
   * @param {ClueBoard} clueBoard - The ClueBoard instance that triggered the event.
   */
  onClueWordFilled(clueWordObj, clueBoard) {
    // Placeholder: implement any logic needed when a clue word is filled.
    // For example, update UI, notify other components, or check completion.
    // Example:
    console.log(
      "Filled ClueWord: " +
        clueWordObj +
        " Clueboard: (" +
        clueBoard.headerLabelText +
        ") Used: " +
        clueBoard.countUsedClueWords()
    );
    // Global logging flag (can be set elsewhere in your app)
    if (window.CLUEBOARD_DEBUG_LOG) {
      this.clueBoards.forEach((board, i) => {
        if (typeof board.countUsedClueWords === "function") {
          console.log(
            `${i + 1} ClueBoard, ${board.headerLabelText} used words:`,
            board.countUsedClueWords()
          );
        }
      });
    }
    if (window.finGame && typeof window.finGame.checkAllClues === "function") {
      const allUsed = window.finGame.checkAllClues();
      if (allUsed) {
        console.log("ClueBoard Controller: Game is solved.");
      }
    }
  }
}
